import React, { useMemo, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const normalizeKey = (value) => (value || "").trim();

const copyToClipboard = async (value) => {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = value;
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand("copy");
  document.body.removeChild(textArea);
};

function CopyableCodeBlock({ text, copied, onCopy }) {
  return (
    <Box
      sx={{
        position: "relative",
        mt: 0.75,
        mb: 1,
      }}
    >
      <Tooltip title={copied ? "Copied" : "Copy"} placement="top">
        <IconButton
          size="small"
          onClick={onCopy}
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            color: copied ? "#0DA3E2" : "#C5C7CA",
            zIndex: 1,
          }}
        >
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
      <Box
        component="pre"
        sx={{
          p: 1,
          pr: 5,
          borderRadius: 1,
          fontSize: 11,
          lineHeight: 1.4,
          whiteSpace: "pre-wrap",
          bgcolor: "rgba(0,0,0,0.28)",
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        }}
      >
        {text}
      </Box>
    </Box>
  );
}

export default function ExternalApiDocsContent({
  externalFields = [],
  flowId,
  brandId,
}) {
  const [copiedBlock, setCopiedBlock] = useState("");
  const apiBaseUrl =
    process.env.REACT_APP_API_URL || "https://api.icreate-campaign.com";
  const normalizedApiBaseUrl = apiBaseUrl.replace(/\/+$/, "");
  const apiBaseWithoutVersion = normalizedApiBaseUrl.replace(/\/Api\/V1$/i, "");
  const exampleExternalFields = useMemo(() => {
    if (!externalFields.length) return {};

    return externalFields.reduce((acc, field) => {
      const key = normalizeKey(field?.key);
      if (!key) return acc;

      if (field.type === "int") acc[key] = 4;
      else if (field.type === "bool") acc[key] = true;
      else if (field.type === "date") acc[key] = "2026-05-04";
      else acc[key] = "sample value";

      return acc;
    }, {});
  }, [externalFields]);

  const exampleRequestBody = useMemo(
    () =>
      JSON.stringify(
        {
          FlowId: Number(flowId) > 0 ? Number(flowId) : 289,
          BrandId: Number(brandId) > 0 ? Number(brandId) : 8816,
          Leads: [
            {
              LeadId: 18520485,
              ...(Object.keys(exampleExternalFields).length > 0
                ? { ExternalFields: exampleExternalFields }
                : {}),
            },
          ],
        },
        null,
        2
      ),
    [brandId, exampleExternalFields, flowId]
  );
  const endpointText = `POST ${apiBaseWithoutVersion}/Api/V1/Journey/TriggerExternalFlow`;
  const headersText = `Content-Type: application/json
X-Brand-Password: your-brand-password`;

  const handleCopy = async (key, value) => {
    await copyToClipboard(value);
    setCopiedBlock(key);
    setTimeout(() => {
      setCopiedBlock((current) => (current === key ? "" : current));
    }, 1200);
  };

  return (
    <Box
      sx={{
        p: 2,
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 1,
        bgcolor: "rgba(255,255,255,0.02)",
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
        How to call this API
      </Typography>
      <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.8)" }}>
        Send a POST request to:
      </Typography>
      <CopyableCodeBlock
        text={endpointText}
        copied={copiedBlock === "endpoint"}
        onCopy={() => handleCopy("endpoint", endpointText)}
      />
      <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.8)" }}>
        Required headers:
      </Typography>
      <CopyableCodeBlock
        text={headersText}
        copied={copiedBlock === "headers"}
        onCopy={() => handleCopy("headers", headersText)}
      />
      <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.8)" }}>
        Example JSON body:
      </Typography>
      <CopyableCodeBlock
        text={exampleRequestBody}
        copied={copiedBlock === "json"}
        onCopy={() => handleCopy("json", exampleRequestBody)}
      />
    </Box>
  );
}
